<script setup>
import {
  StealieButton,
  StealieHeading,
  StealieOverline,
  StealieTextInput,
  StealieParagraph,
} from "@get-overalls/fe-stealie";
import SignInLayout from "@/layouts/SignInLayout.vue";
import FormError from "@/components/FormError.vue";
import { useMfaEnrollment } from "./composables/useMfaEnrollment";

const {
  mfaPhoneNumber,
  errors,
  submitError,
  isSubmitting,
  mfaVerificationRequired,
  mfaCode,
  handleMfaEnrollmentSubmit,
  handleMfaVerificationSubmit,
  resendCode,
} = useMfaEnrollment();
</script>

<template>
  <SignInLayout>
    <div>
      <div>
        <StealieOverline> Multi-Factor Authentication </StealieOverline>
      </div>
      <div v-if="!mfaVerificationRequired">
        <div class="col-span-4">
          <StealieHeading size="lg" class="mb-6 text-balance">
            Let's get you set up!
          </StealieHeading>
        </div>
        <StealieParagraph id="mfa-description" class="mb-6">
          If you'd like to add multi-factor authentication to your account, we'll use this
          mobile number as a second method of checking your identity when you log in to
          Overalls.
        </StealieParagraph>
        <form
          aria-label="Multi-factor authentication enrollment form"
          method="post"
          novalidate
          class="flex flex-col gap-6"
          @submit.prevent="handleMfaEnrollmentSubmit"
        >
          <div>
            <StealieTextInput
              v-model="mfaPhoneNumber"
              name="mfaPhoneNumber"
              type="tel"
              label="Phone Number"
              autocomplete="tel-national"
              placeholder="2125551212"
              required
              :error-message="errors.mfaPhoneNumber"
            />
          </div>
          <FormError v-if="submitError" :error="submitError" />
          <div class="flex items-center justify-between gap-x-4">
            <StealieButton type="submit" class="flex-1" :loading="isSubmitting">
              Verify
            </StealieButton>
          </div>
        </form>
      </div>
      <div v-else>
        <div class="col-span-4">
          <StealieHeading size="lg" class="mb-6 text-balance">
            Almost done!
          </StealieHeading>
        </div>
        <StealieParagraph id="mfa-description" class="mb-4">
          We sent a verification code to the phone number you provided. Please enter the
          code below to continue setting up multi-factor authentication for your account.
        </StealieParagraph>
        <form
          aria-label="Multi-factor authentication verification form"
          method="post"
          novalidate
          class="flex flex-col gap-6"
          @submit.prevent="handleMfaVerificationSubmit"
        >
          <div>
            <StealieTextInput
              v-model="mfaCode"
              name="mfaVerificationCode"
              type="text"
              label="Verification Code"
              aria-describedby="mfa-description"
              autocomplete="one-time-code"
              autofocus
              required
              :error-message="errors.mfaVerificationCode"
            />
          </div>
          <FormError v-if="submitError" :error="submitError" />
          <div>
            <StealieButton
              type="submit"
              class="mt-2 mb-6"
              full-width
              :loading="isSubmitting"
            >
              Submit
            </StealieButton>
          </div>
        </form>
        <div>
          <StealieParagraph size="sm" class="mb-4">
            Haven't received the code?
            <StealieButton variant="tertiary" @click="resendCode"
              >Resend code</StealieButton
            >
          </StealieParagraph>
        </div>
      </div>
    </div>
  </SignInLayout>
</template>
